// @flow

import _ from 'lodash';

import pageDefinitionsMap from 'app/beta-editor/pages/definitions';
import { getNodesForPath } from 'app/beta-editor/utils/PathUtils';
import {
  createEditorUrl,
  isFlowTemplate,
} from 'app/beta-editor/utils/FlowUtils';

import type { AuthMap } from 'app/auth/types';
import type { Flow, FlowNode } from 'app/beta-editor/flowState';
import type {
  FlowPage,
  FlowPageKey,
  SimpleZapPageKey,
} from 'app/beta-editor/types';
import type { ID } from 'app/common/types';
import type { ServicesMap } from 'app/service/types';

///// Exports /////
const pages = {};

pages.hasContextFor = function(pageKey: FlowPageKey | SimpleZapPageKey) {
  return _.has(pageDefinitionsMap, pageKey);
};

type VisibleSubStepsProps = {
  auths: AuthMap,
  flow: Flow,
  isNodeWaiting?: boolean,
  isZapTemplate: boolean,
  node: FlowNode,
  nodeId: ID,
  services: ServicesMap,
  shouldUseSimpleZapSetup: boolean,
};

type ContextForProps = {
  ...$Exact<VisibleSubStepsProps>,
  pageKey: FlowPageKey,
};

const toProperPhrase = (phrase: ?string) =>
  (phrase || '')
    .split(' ')
    .map(word => _.capitalize(word))
    .join(' ');

pages.contextFor = (props: ContextForProps): FlowPage => {
  const { pageKey } = props;
  const { behavior } =
    pageDefinitionsMap[pageKey] || pageDefinitionsMap.overview;
  const isTemplate = isFlowTemplate(props.flow.nodes);

  return {
    key: props.pageKey,
    label: toProperPhrase(behavior.label(props)),
    menuLabel: toProperPhrase(behavior.menuLabel(props)),
    crumbLabel: behavior.crumbLabel(props),
    data: behavior.data(props),
    status: behavior.status(props),
    visible: behavior.visible(props), // Should page appear in the nav menu for the node
    isClickable: behavior.isClickable(props), // Is page useable right now. Useful to disable a step when we are waiting for server-side validation to finish
    skipDuringWalkthrough: behavior.skipDuringWalkthrough
      ? behavior.skipDuringWalkthrough(props)
      : false,
    isLoading: behavior.isLoading(props),
    isLocked: behavior.isLocked(props), // Is page locked due to state of parent nodes
    href: behavior.href
      ? behavior.href(props)
      : createEditorUrl({
          flowId: props.flow.id,
          nodeId: props.node.id,
          pageKey: props.pageKey,
          isTemplate,
          query: { simple: props.shouldUseSimpleZapSetup },
        }),
    nodeId: props.node.id,
  };
};

/*
   Returns page context objects for all visible sub-steps of a node. Does _not_
   include the overview page.
*/

type AllPages = Array<FlowPage>;

pages.visibleSubSteps = (props: VisibleSubStepsProps) => {
  const pageDefinitionsKeys = Object.keys(pageDefinitionsMap);
  const allPages: AllPages = pageDefinitionsKeys.map(pageKey =>
    pages.contextFor({
      auths: props.auths,
      flow: props.flow,
      isNodeWaiting: props.isNodeWaiting,
      isZapTemplate: props.isZapTemplate,
      node: props.node,
      nodeId: props.nodeId,
      pageKey,
      services: props.services,
      shouldUseSimpleZapSetup: props.shouldUseSimpleZapSetup,
    })
  );
  return allPages.filter(page => page.visible && page.key !== 'overview');
};

pages.getNextStepInNode = (props: VisibleSubStepsProps) => {
  return pages
    .visibleSubSteps(props)
    .find(page => !page.skipDuringWalkthrough && page.status !== 'success');
};

type FlowPageContextProps = {
  ...$Exact<VisibleSubStepsProps>,
  currentHref?: string,
  nodeId: ID,
};

type FlowPageContext = Array<{
  ...$Exact<FlowPage>,
  nodeId: ID,
}>;

type SimpleZapPageContext = Array<{
  ...$Exact<FlowPage>,
  key: SimpleZapPageKey,
  nodeId: ID,
}>;

type FlowPageContexts = {
  [nodeId: ID]: FlowPageContext,
};

export type SimpleZapPageContexts = {
  [nodeId: ID]: SimpleZapPageContext,
};

/*
   Return map of arrays of page contexts for visible sub-steps, keyed
   by node id. Zero or one of the page contexts will be marked as
   'isNext'.

   If nodeId is specified, restrict steps to that node.
 */
pages.flowPageContexts = (props: FlowPageContextProps) => {
  const { currentHref, flow, shouldUseSimpleZapSetup } = props;

  const { nodes, steps } = flow;

  if (shouldUseSimpleZapSetup) {
    return pages.simpleZapContexts(props);
  }

  const contexts = {};

  const allPages = steps
    ? steps.reduce((arr, currStep) => {
        const node = nodes[currStep.nodeId];
        const pageContexts = pages.visibleSubSteps({
          auths: props.auths,
          flow,
          isNodeWaiting: props.isNodeWaiting,
          isZapTemplate: props.isZapTemplate,
          node,
          nodeId: props.nodeId,
          services: props.services,
          shouldUseSimpleZapSetup: props.shouldUseSimpleZapSetup,
        });
        contexts[currStep.nodeId] = pageContexts;
        return arr.concat(pageContexts);
      }, [])
    : [];

  let next;
  if (currentHref) {
    // given a current href, the next step is the one immediately after
    const currIndex = _.findIndex(allPages, page => page.href === currentHref);
    const nextPages = _.slice(allPages, currIndex + 1);
    next = _.find(
      nextPages,
      page => !page.skipDuringWalkthrough && !page.isLocked
    );
    if (next) {
      next.isNext = true;
    }
  } else {
    // without a current href, we're probably on the overview screen so
    // figure out what the next incomplete step is
    // Set 0 or 1 of the page contexts to be 'next'
    const notSkippedPages = _.filter(
      allPages,
      page => !page.skipDuringWalkthrough
    );
    next = _.find(notSkippedPages, page => page.status !== 'success');
    if (next) {
      next.isNext = true;
    }
  }

  return contexts;
};

pages.simpleZapContexts = (props: VisibleSubStepsProps) => {
  const { flow } = props;

  return flow.steps.reduce((acc, currStep) => {
    const node = flow.nodes[currStep.nodeId];
    const pageContexts = pages.visibleSubSteps({
      auths: props.auths,
      flow,
      isNodeWaiting: props.isNodeWaiting,
      isZapTemplate: props.isZapTemplate,
      node,
      nodeId: props.nodeId,
      services: props.services,
      shouldUseSimpleZapSetup: props.shouldUseSimpleZapSetup,
    });

    acc[currStep.nodeId] = pageContexts.reduce((arr, context) => {
      const { key } = context;
      // href = null so that editor won't try to route to next page on componentDidUpdate
      if (key === 'auth' || key === 'fields' || key === 'webhook') {
        arr = [...arr, { ...context, isNext: true, href: null }];
      }

      return arr;
    }, []);

    const contextByNode = acc[currStep.nodeId];
    const noAuthNeeded =
      contextByNode.length === 0 ||
      contextByNode.every(context => context.key !== 'auth');
    const noWebhook =
      contextByNode.length === 0 ||
      contextByNode.every(context => context.key !== 'webhook');

    const createAuthlessPlaceholder = (result, nodeId, context) => ({
      ...result,
      [currStep.nodeId]: [
        { key: 'authless', nodeId: currStep.nodeId },
        ...context,
      ],
    });

    // If a Zap trigger has a webhook step, we hide auth components, including the disabled authless placeholder
    return noAuthNeeded && noWebhook
      ? createAuthlessPlaceholder(acc, currStep.nodeId, contextByNode)
      : acc;
  }, {});
};

/*
  Return page context object for the next uncompleted sub-step page of one node,
  or null if all sub-steps are complete for the node.

  ^ THAT'S A LIE (use pages.nextPageInNode instead if you want that behavior)

  If you supply a current href, isNext will be set to the immediate next step
  otherwise it'll be the global next incompleted step for this node (step).
*/

type NextPageProps = {
  auths: AuthMap,
  currentHref: string,
  flow: Flow,
  isZapTemplate: boolean,
  nodeId: ID,
  services: ServicesMap,
  shouldUseSimpleZapSetup: boolean,
};

pages.nextPage = (props: NextPageProps) => {
  const node = props.flow.nodes[props.nodeId];
  const pageContexts = pages.flowPageContexts({
    auths: props.auths,
    currentHref: props.currentHref,
    flow: props.flow,
    isZapTemplate: props.isZapTemplate,
    node,
    nodeId: props.nodeId,
    services: props.services,
    shouldUseSimpleZapSetup: props.shouldUseSimpleZapSetup,
  });
  return _.find(pageContexts, p => p.isNext);
};

/*
  The comment up there is lying. But I don't want to break everything using it. So I'm making a function that actually
  gives you the next page for a node.
*/

pages.nextPageInNode = (props: FlowPageContextProps) => {
  const pageContexts: FlowPageContexts = pages.flowPageContexts(props);
  const currentPageContext = pageContexts[props.nodeId];
  if (currentPageContext) {
    return currentPageContext.find(page => {
      return (
        !page.skipDuringWalkthrough && page.status !== 'success' && page.visible
      );
    });
  } else {
    return undefined;
  }
};

/*
   Return the next page / sub-step in a zap, progressing to next step if necessary.
   Does not matter if the next page is completed or not.

   Returns null if this if currentHref is for the very last sub-step of the last step of zap.
*/

pages.nextPageInZap = (_props: FlowPageContextProps) => {
  const { nodeId, ...props } = _props;
  const allPages = pages.flowPageContexts(_props);

  const nodes = getNodesForPath(props.flow.id, props.flow.nodes, nodeId);

  const nextPage = _.find(allPages[nodeId], p => p.isNext);
  const index = _.findIndex(nodes, node => String(node.id) === String(nodeId));

  if (nextPage) {
    return nextPage;
  } else if (nodes[index + 1]) {
    const nextNodeId = nodes[index + 1].id;
    return (allPages[nextNodeId] && allPages[nextNodeId][0]) || null;
  } else {
    return null;
  }
};

/*
  Return the next sub-step page, given a currentHref.
*/
export function getNextPageOrUndefined(props: FlowPageContextProps) {
  const node = props.flow.nodes[props.nodeId];
  const pageContexts = _.flatten(
    _.values(
      pages.flowPageContexts({
        auths: props.auths,
        currentHref: props.currentHref,
        flow: props.flow,
        isNodeWaiting: props.isNodeWaiting,
        isZapTemplate: props.isZapTemplate,
        node,
        nodeId: props.nodeId,
        services: props.services,
        shouldUseSimpleZapSetup: props.shouldUseSimpleZapSetup,
      })
    )
  );

  return _.find(pageContexts, p => p.isNext);
}

/*
   Return next uncompleted page (sub-step) in entire zap.
*/
pages.nextIncompletePageInZap = (props: FlowPageContextProps) => {
  const node = props.flow.nodes[props.nodeId];

  const nextPage = getNextPageOrUndefined(props);
  if (nextPage) {
    return nextPage;
  }

  return pages.contextFor({
    auths: props.auths,
    flow: props.flow,
    isNodeWaiting: props.isNodeWaiting,
    isZapTemplate: props.isZapTemplate,
    node,
    nodeId: props.nodeId,
    pageKey: 'setup',
    services: props.services,
    shouldUseSimpleZapSetup: props.shouldUseSimpleZapSetup,
  });
};

export default pages;
